/*
 * Copyright Higharc Inc. 2020
 */
import { productLog } from "common/utils/logging";
import * as THREE from "three";
import { FullScreenQuad } from "../examples/jsm/postprocessing/FullScreenQuad";
import { DepthToViewZShader } from "./DepthToViewZShader";

export function testDepthTextureBitDepth(renderer: THREE.WebGLRenderer, depthTextureType: THREE.TextureDataType) {
  const textureWidth = 1;
  const textureHeight = 256;
  const depthTexture = new THREE.DepthTexture(textureWidth, textureHeight, depthTextureType);
  const renderTarget = new THREE.WebGLRenderTarget(textureWidth, textureHeight, {
    minFilter: THREE.NearestFilter,
    magFilter: THREE.NearestFilter,
    format: THREE.RGBAFormat,
  });
  renderTarget.depthTexture = depthTexture;
  renderer.setRenderTarget(renderTarget);

  // Render into the depth texture and see if we actually get results indicating a high bit depth.
  const scene = new THREE.Scene();

  // 1. Set up a scene with a single ground plane
  const material = new THREE.MeshBasicMaterial();
  const geometry = new THREE.PlaneGeometry(400, 400);
  const mesh = new THREE.Mesh(geometry, material);
  scene.add(mesh);

  // 2. Set up a camera so that the ground plane fills the viewport.
  // The camera is looking diagonally down towards the ground plane from an elevated position (z-up coordinate system).
  const camera = new THREE.PerspectiveCamera(70, textureWidth / textureHeight, 1, 1000);
  camera.position.set(0, 100, 50);
  camera.up.set(0, 0, 1);
  camera.lookAt(0, 50, 0);

  renderer.render(scene, camera);

  // 3. Convert depth to color so we can read it.
  const depthTextureToViewZMaterial = new THREE.ShaderMaterial({
    defines: { DEPTH_PACKING: 0, SCALE_DEPTH_VALUES: 1 },
    uniforms: THREE.UniformsUtils.clone(DepthToViewZShader.uniforms),
    vertexShader: DepthToViewZShader.vertexShader,
    fragmentShader: DepthToViewZShader.fragmentShader,
    blending: THREE.NoBlending,
    name: "testDepthTextureBitDepth depthTextureToViewZMaterial",
  });

  depthTextureToViewZMaterial.uniforms["tDepth"].value = depthTexture;
  depthTextureToViewZMaterial.uniforms["cameraNear"].value = camera.near;
  depthTextureToViewZMaterial.uniforms["cameraFar"].value = camera.far;
  depthTextureToViewZMaterial.uniforms["perspectiveCamera"].value = camera.isPerspectiveCamera;
  depthTextureToViewZMaterial.uniforms["relativeViewZMin"].value = (8 / 255) * (1000 / camera.far);
  depthTextureToViewZMaterial.uniforms["relativeViewZMax"].value = (64 / 255) * (1000 / camera.far);

  const colorRenderTarget = new THREE.WebGLRenderTarget(textureWidth, textureHeight, {
    minFilter: THREE.NearestFilter,
    magFilter: THREE.NearestFilter,
    format: THREE.RGBAFormat,
  });
  renderer.setRenderTarget(colorRenderTarget);

  const fsQuad = new FullScreenQuad(depthTextureToViewZMaterial);
  fsQuad.render(renderer);

  const readBuffer = new Uint8Array(textureWidth * textureHeight * 4);
  renderer.readRenderTargetPixels(colorRenderTarget, 0, 0, textureWidth, textureHeight, readBuffer);
  let quantizationDetected = false;
  for (let i = 2; i < textureHeight; ++i) {
    if (readBuffer[i * 4] === readBuffer[(i - 1) * 4] && readBuffer[(i - 1) * 4] > readBuffer[(i - 2) * 4] + 1) {
      // Two consecutive values in the readBuffer are the same despite the depth gradient not allowing it.
      // For example, this condition would trigger if consecutive depth values are 1, 3, 3, the jump from 1 to 3
      // indicating quantization.
      productLog("Depth texture quantization detected");
      quantizationDetected = true;
      break;
    }
  }

  depthTextureToViewZMaterial.dispose();
  colorRenderTarget.dispose();

  renderer.setRenderTarget(null);
  depthTexture.dispose();
  renderTarget.dispose();
  geometry.dispose();
  return !quantizationDetected;
}
